import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
};

/**
 * Lightbox — full-screen viewer for one project's image carousel.
 * Props:
 *   images:     array of image URLs (strings)
 *   overlays:   optional array of per-image caption text (same length as images)
 *   currentIndex: which image index is currently shown
 *   title, description, location, year: project metadata (shown under the image)
 *   onClose, onPrevious, onNext: handlers
 */
export const Lightbox = ({
  images = [],
  overlays = [],
  currentIndex = 0,
  title = '',
  description = '',
  location = '',
  year = '',
  onClose,
  onPrevious,
  onNext,
}) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrevious();
      else if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onPrevious, onNext]);

  if (currentIndex === null || !images[currentIndex]) return null;

  const currentImage = images[currentIndex];
  const currentOverlay = overlays[currentIndex] || '';
  const total = images.length;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Project image viewer'}
      data-testid="lightbox"
    >
      {/* Close */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 text-white hover:bg-white/10 z-10"
        onClick={onClose}
        data-testid="lightbox-close-btn"
      >
        <X className="h-6 w-6" />
      </Button>

      {/* Prev */}
      {total > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 text-white hover:bg-white/10 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          data-testid="lightbox-prev-btn"
        >
          <ChevronLeft className="h-8 w-8" />
        </Button>
      )}

      {/* Next */}
      {total > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 text-white hover:bg-white/10 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          data-testid="lightbox-next-btn"
        >
          <ChevronRight className="h-8 w-8" />
        </Button>
      )}

      {/* Image + Info */}
      <div className="max-w-7xl max-h-[90vh] mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <img
            src={cldPresets.galleryLightbox(resolveUrl(currentImage))}
            alt={title}
            className="max-w-full max-h-[80vh] object-contain mx-auto rounded-lg"
          />
          {currentOverlay && (
            <div className="absolute inset-x-0 bottom-0 px-6 py-3 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
              <p className="text-white font-semibold drop-shadow" data-testid="lightbox-overlay">
                {currentOverlay}
              </p>
            </div>
          )}
        </div>

        <div className="text-center mt-4 text-white">
          <h3 className="text-2xl font-bold mb-2">{title}</h3>
          {description && <p className="text-gray-300">{description}</p>}
          <div className="flex justify-center items-center space-x-4 mt-2 text-sm text-gray-400">
            {location && <span>{location}</span>}
            {year && <span>• {year}</span>}
            <span data-testid="lightbox-counter">• {currentIndex + 1} / {total}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
